import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './port-create-panel.css';

/**
 * PortCreatePanel — slide-in панель создания сетевого порта
 * По макету Figma. Overlay + правая панель 480 px.
 *
 * Заменяет Ant Design Modal для создания.
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = ({ title }) => (
  <svg
    className="pcp__info-icon"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={title}
  >
    <title>{title}</title>
    <circle cx="6" cy="6" r="5.25" stroke="currentColor" strokeWidth="1" />
    <path d="M6 5.5V8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    <circle cx="6" cy="3.75" r="0.6" fill="currentColor" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 7.5L6 5L8.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 4.5L6 7L8.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SelectChevron = () => (
  <svg className="pcp__select-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 4.5L6 7L8.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== DEFAULTS ====================

const DEFAULTS = {
  connection_id: undefined,
  name: '',
  ifname: undefined,
  type: 4,
  active: true,
  capacity_speed_mbps: 26,
  max_tcp_connections: 1000,
  safety_buffer: 100,
  monitoring_interval: 3,
  net_mode: 'lte',
  reconnect_type: 'time_link',
  reconnect_interval: 3,
  reconnect_min: 20,
  mtu: 0,
  is_osfp: false,
  osfp: undefined,
  // Hidden Kraken API defaults
  reconnect_event: ['timeout'],
  timeout: 30,
  params: '{}',
  users: [],
};

// ==================== SELECT OPTIONS ====================

const NET_MODE_OPTIONS = [
  { value: 'lte', label: 'LTE' },
  { value: 'umts', label: 'UMTS (3G)' },
];

const RECONNECT_TYPE_OPTIONS = [
  { value: 'time', label: 'По времени' },
  { value: 'link', label: 'Ссылка' },
  { value: 'time_link', label: 'Время + ссылка' },
];

// ==================== SUB-COMPONENTS ====================

const NumberField = ({ label, required, value, onChange, onKeyDown, min, max, step = 1, infoTitle }) => {
  const handleIncrement = () => {
    const num = Number(value) || 0;
    const next = Math.min(num + step, max ?? Infinity);
    onChange(next);
  };

  const handleDecrement = () => {
    const num = Number(value) || 0;
    const next = Math.max(num - step, min ?? -Infinity);
    onChange(next);
  };

  return (
    <div className="pcp__field">
      <div className="pcp__label">
        {required && <span className="pcp__label-required">*</span>}
        <span className="pcp__label-text">{label}</span>
        {infoTitle && <InfoIcon title={infoTitle} />}
      </div>
      <div className="pcp__number-wrap">
        <input
          type="number"
          className="pcp__input pcp__input--number"
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') { onChange(''); return; }
            let num = Number(raw);
            if (isNaN(num)) return;
            if (min !== undefined && num < min) num = min;
            if (max !== undefined && num > max) num = max;
            onChange(num);
          }}
          onKeyDown={onKeyDown}
          min={min}
          max={max}
          step={step}
          aria-label={label}
        />
        <div className="pcp__number-arrows">
          <button
            type="button"
            className="pcp__number-arrow pcp__number-arrow--up"
            onClick={handleIncrement}
            tabIndex={-1}
            aria-label="Увеличить"
          >
            <ChevronUpIcon />
          </button>
          <button
            type="button"
            className="pcp__number-arrow pcp__number-arrow--down"
            onClick={handleDecrement}
            tabIndex={-1}
            aria-label="Уменьшить"
          >
            <ChevronDownIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleRow = ({ label, checked, onChange }) => (
  <div className="pcp__toggle-row">
    <span className="pcp__toggle-label">{label}</span>
    <button
      type="button"
      className={`pcp__toggle${checked ? ' pcp__toggle--on' : ''}`}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      tabIndex={0}
    >
      <span className="pcp__toggle-dot" />
    </button>
  </div>
);

/**
 * Custom select / dropdown — replaces Ant Design Select.
 * Supports search, loading state, disabled.
 */
const SelectField = ({
  label,
  required,
  placeholder,
  value,
  onChange,
  options = [],
  loading = false,
  disabled = false,
  error,
  searchable = true,
  infoTitle,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Focus search on open
  useEffect(() => {
    if (open && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open, searchable]);

  const selectedOption = options.find(o => o.value === value);
  const displayText = selectedOption ? selectedOption.label : null;

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  const triggerClasses = [
    'pcp__select-trigger',
    !displayText ? 'pcp__select-trigger--placeholder' : '',
    disabled ? 'pcp__select-trigger--disabled' : '',
    error ? 'pcp__select-trigger--error' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="pcp__field">
      <div className="pcp__label">
        {required && <span className="pcp__label-required">*</span>}
        <span className="pcp__label-text">{label}</span>
        {infoTitle && <InfoIcon title={infoTitle} />}
      </div>
      <div className={`pcp__select-wrap${open ? ' pcp__select-wrap--open' : ''}`} ref={wrapRef}>
        <button
          type="button"
          className={triggerClasses}
          onClick={() => { if (!disabled) setOpen(!open); }}
          aria-label={label}
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        >
          <span className="pcp__select-value">
            {displayText || placeholder || 'Выберите...'}
          </span>
          <SelectChevron />
        </button>

        {open && (
          <div className="pcp__select-dropdown" role="listbox">
            {searchable && (
              <input
                ref={searchRef}
                type="text"
                className="pcp__select-search"
                placeholder="Поиск..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {loading && <div className="pcp__select-empty">Загрузка...</div>}
            {!loading && filtered.length === 0 && (
              <div className="pcp__select-empty">Ничего не найдено</div>
            )}
            {!loading && filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`pcp__select-option${opt.value === value ? ' pcp__select-option--active' : ''}`}
                onClick={() => handleSelect(opt.value)}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <span className="pcp__field-error">{error}</span>}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const PortCreatePanel = ({
  visible = false,
  onClose = null,
  onSubmit = null,
  submitting = false,
  krakenConnections = [],
  loadingConnections = false,
  networkInterfaces = [],
  loadingInterfaces = false,
  modemTypes = [],
  ports = [],
  onConnectionChange = null,
  tcpFingerprints = [],
  loadingTcpFingerprints = false,
}) => {
  const [formData, setFormData] = useState({ ...DEFAULTS });
  const [errors, setErrors] = useState({});

  // ==================== RESET ON OPEN ====================
  useEffect(() => {
    if (visible) {
      setFormData({ ...DEFAULTS });
      setErrors({});
    }
  }, [visible]);

  // ==================== HANDLERS ====================
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.connection_id) {
      newErrors.connection_id = 'Выберите подключение Кракен';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Введите название порта';
    }
    if (!formData.ifname) {
      newErrors.ifname = 'Выберите сетевой интерфейс';
    }
    if (!formData.type && formData.type !== 0) {
      newErrors.type = 'Выберите тип модема';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    if (!onSubmit) return;

    const safetyDecimal = Number(formData.safety_buffer) > 1
      ? Number(formData.safety_buffer) / 100
      : Number(formData.safety_buffer) || 1.0;
    const parsedMaxTcpConnections = Number(formData.max_tcp_connections);
    const normalizedMaxTcpConnections = formData.max_tcp_connections === ''
      ? 1000
      : (Number.isFinite(parsedMaxTcpConnections) ? parsedMaxTcpConnections : 1000);

    const values = {
      connection_id: formData.connection_id,
      name: formData.name.trim(),
      ifname: formData.ifname,
      type: Number(formData.type),
      active: formData.active,
      capacity_speed_mbps: Number(formData.capacity_speed_mbps) || 26,
      max_tcp_connections: normalizedMaxTcpConnections,
      safety_buffer: safetyDecimal,
      monitoring_interval: Number(formData.monitoring_interval) || 3,
      net_mode: formData.net_mode || 'lte',
      reconnect_type: formData.reconnect_type || 'time_link',
      reconnect_interval: Number(formData.reconnect_interval) || 240,
      reconnect_min: Number(formData.reconnect_min) || 20,
      mtu: Number(formData.mtu) || 0,
      is_osfp: formData.is_osfp,
      osfp: formData.is_osfp ? (formData.osfp || null) : null,
      reconnect_event: formData.reconnect_event || ['timeout'],
      timeout: Number(formData.timeout) || 30,
      params: formData.params || '{}',
      users: formData.users || [],
    };

    onSubmit(values);
  }, [formData, validate, onSubmit]);

  const handleClose = useCallback(() => {
    if (!submitting && onClose) onClose();
  }, [submitting, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleConnectionChange = useCallback((connectionId) => {
    handleChange('connection_id', connectionId);
    handleChange('ifname', undefined);
    if (onConnectionChange) onConnectionChange(connectionId);
  }, [handleChange, onConnectionChange]);

  // ==================== DERIVED OPTIONS ====================

  const connectionOptions = krakenConnections.map(c => ({
    value: c.id,
    label: c.alias ? `${c.name} (${c.alias})` : c.name,
  }));

  const usedIfnames = formData.connection_id
    ? [...new Set(ports.filter(p => p.connection_id === formData.connection_id).map(p => p.ifname).filter(Boolean))]
    : [];

  const interfaceOptions = networkInterfaces
    .filter(iface => {
      const val = typeof iface === 'string' ? iface : (iface.name || iface.ifname || iface.interface || iface.id);
      return !usedIfnames.includes(val);
    })
    .map(iface => {
      const label = iface.name || iface.ifname || iface.interface || iface;
      const value = typeof iface === 'string' ? iface : (iface.name || iface.ifname || iface.interface || iface.id);
      return { value, label: String(label) };
    });

  const fingerprintOptions = tcpFingerprints.map(fp => ({
    value: fp.id,
    label: fp.name,
  }));

  const modemTypeOptions = modemTypes.length > 0
    ? modemTypes.map(t => ({ value: t.id, label: t.name || `Type ${t.id}` }))
    : [
        { value: 1, label: 'Huawei E3372h-153' },
        { value: 2, label: 'ZTE MF833R' },
        { value: 3, label: 'ZTE MF920RU' },
        { value: 4, label: 'Huawei E3372h-320' },
      ];

  // ==================== EARLY RETURN ====================
  if (!visible) return null;

  return createPortal(
    <div
      className="pcp-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="pcp" role="dialog" aria-label="Создание сетевого порта">

        {/* ==================== HEADER ==================== */}
        <div className="pcp__header">
          <h2 className="pcp__title">создание сетевого порта</h2>
          <button
            type="button"
            className="pcp__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* ==================== BODY ==================== */}
        <div className="pcp__body">

          {/* --- Section: Основные настройки --- */}
          <div className="pcp__section">
            <div className="pcp__section-title">Основные настройки</div>

            {/* Row: Подключение Кракен + Название порта */}
            <div className="pcp__row">
              <SelectField
                label="Подключение Кракен"
                required
                placeholder="Выберите подключение"
                value={formData.connection_id}
                onChange={handleConnectionChange}
                options={connectionOptions}
                loading={loadingConnections}
                error={errors.connection_id}
              />
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-required">*</span>
                  <span className="pcp__label-text">Название порта</span>
                </div>
                <input
                  type="text"
                  className={`pcp__input${errors.name ? ' pcp__input--error' : ''}`}
                  placeholder="Введите название"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label="Название порта"
                  aria-required="true"
                  autoComplete="off"
                />
                {errors.name && <span className="pcp__field-error">{errors.name}</span>}
              </div>
            </div>

            {/* Row: Сетевой интерфейс + Тип модема */}
            <div className="pcp__row">
              <SelectField
                label="Сетевой интерфейс"
                required
                placeholder="Выберите сетевой интерфейс"
                value={formData.ifname}
                onChange={(v) => handleChange('ifname', v)}
                options={interfaceOptions}
                loading={loadingInterfaces}
                disabled={!formData.connection_id}
                error={errors.ifname}
              />
              <SelectField
                label="Тип модема"
                required
                placeholder="Выберите тип модема"
                value={formData.type}
                onChange={(v) => handleChange('type', v)}
                options={modemTypeOptions}
                loading={modemTypes.length === 0}
                error={errors.type}
              />
            </div>

            {/* Toggle: Активность порта */}
            <ToggleRow
              label="Активность порта"
              checked={formData.active}
              onChange={(v) => handleChange('active', v)}
            />
          </div>

          {/* --- Section: Характеристики производительности --- */}
          <div className="pcp__section">
            <div className="pcp__section-title">Характеристики производительности</div>

            {/* Row: Скорость + Макс TCP */}
            <div className="pcp__row">
              <NumberField
                label="Скорость (емкость) в Мб/с"
                required
                value={formData.capacity_speed_mbps}
                onChange={(v) => handleChange('capacity_speed_mbps', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={1000}
                infoTitle="Максимальная пропускная способность порта в Мб/с"
              />
              <NumberField
                label="Макс. TCP соединений"
                required
                value={formData.max_tcp_connections}
                onChange={(v) => handleChange('max_tcp_connections', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={10000}
                infoTitle="Максимальное количество одновременных TCP соединений"
              />
            </div>

            {/* Row: Буфер безопасности (single) */}
            <div className="pcp__row">
              <NumberField
                label="Буфер безопасности (%)"
                required
                value={formData.safety_buffer}
                onChange={(v) => handleChange('safety_buffer', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={100}
              />
            </div>
          </div>

          {/* --- Section: Настройки мониторинга --- */}
          <div className="pcp__section pcp__section--grow">
            <div className="pcp__section-title">Настройки мониторинга</div>

            {/* Row: Интервал мониторинга + Тип сети */}
            <div className="pcp__row">
              <NumberField
                label="Интервал мониторинга (сек)"
                required
                value={formData.monitoring_interval}
                onChange={(v) => handleChange('monitoring_interval', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={300}
                infoTitle="Интервал опроса статуса модема в секундах"
              />
              <SelectField
                label="Тип сети"
                required
                value={formData.net_mode}
                onChange={(v) => handleChange('net_mode', v)}
                options={NET_MODE_OPTIONS}
                searchable={false}
                infoTitle="Тип мобильной сети модема"
              />
            </div>

            {/* Row: Тип переподключения модема + интервал */}
            <div className="pcp__row">
              <SelectField
                label="Тип переподключения модема"
                required
                value={formData.reconnect_type}
                onChange={(v) => handleChange('reconnect_type', v)}
                options={RECONNECT_TYPE_OPTIONS}
                searchable={false}
              />
              <NumberField
                label="Интервал переподключения модема (сек)"
                required
                value={formData.reconnect_interval}
                onChange={(v) => handleChange('reconnect_interval', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={3600}
              />
            </div>

            {/* Row: Минимальный шаг + MTU */}
            <div className="pcp__row">
              <NumberField
                label="Минимальный шаг (сек)"
                required
                value={formData.reconnect_min}
                onChange={(v) => handleChange('reconnect_min', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={300}
              />
              <NumberField
                label="MTU (0 - по умолчанию)"
                required
                value={formData.mtu}
                onChange={(v) => handleChange('mtu', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={1500}
              />
            </div>

            {/* Toggle: Подмена TCP отпечатка */}
            <ToggleRow
              label="Использовать подмену TCP отпечатка"
              checked={formData.is_osfp}
              onChange={(v) => {
                handleChange('is_osfp', v);
                if (!v) handleChange('osfp', undefined);
              }}
            />

            {/* Select: TCP отпечаток (показывается только при is_osfp = true) */}
            {formData.is_osfp && (
              <div className="pcp__row">
                <SelectField
                  label="TCP отпечаток"
                  placeholder={formData.connection_id ? 'Выберите TCP отпечаток' : 'Сначала выберите подключение'}
                  value={formData.osfp}
                  onChange={(v) => handleChange('osfp', v)}
                  options={fingerprintOptions}
                  loading={loadingTcpFingerprints}
                  disabled={!formData.connection_id}
                />
              </div>
            )}
          </div>
        </div>

        {/* ==================== FOOTER ==================== */}
        <div className="pcp__footer">
          <button
            type="button"
            className="pcp__btn pcp__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Отмена"
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="pcp__btn pcp__btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
            aria-label="Создать"
            tabIndex={0}
          >
            {submitting ? 'Создание...' : 'Создать'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PortCreatePanel;
